import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Contrast as ContrastIcon,
  Factory as FactoryIcon,
  Check as CheckIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Theme icon mapping
 */
const THEME_ICONS = {
  dark: DarkModeIcon,
  light: LightModeIcon,
  highContrast: ContrastIcon,
  industrial: FactoryIcon,
};

/**
 * ThemeSelector Component
 *
 * Dropdown menu for selecting application theme.
 * Can also be used as a simple toggle button.
 *
 * @param {object} props
 * @param {boolean} props.showMenu - If true, shows dropdown menu. If false, cycles through themes on click.
 * @param {string} props.size - Icon button size ('small', 'medium', 'large')
 */
const ThemeSelector = ({ showMenu = true, size = 'medium' }) => {
  const { themeId, colors, setTheme, cycleTheme, availableThemes } = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (showMenu) {
      setAnchorEl(event.currentTarget);
    } else {
      cycleTheme();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectTheme = (newThemeId) => {
    setTheme(newThemeId);
    handleClose();
  };

  // Get current theme icon
  const CurrentIcon = THEME_ICONS[themeId] || PaletteIcon;

  return (
    <>
      <Tooltip title={showMenu ? 'Change theme' : `Theme: ${availableThemes.find(t => t.id === themeId)?.name}`}>
        <IconButton
          onClick={handleClick}
          size={size}
          sx={{
            color: colors.text.secondary,
            '&:hover': {
              color: colors.text.primary,
              bgcolor: colors.hover.background,
            },
          }}
          aria-label="theme selector"
          aria-controls={open ? 'theme-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <CurrentIcon />
        </IconButton>
      </Tooltip>

      {showMenu && (
        <Menu
          id="theme-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'theme-selector-button',
          }}
          PaperProps={{
            sx: {
              bgcolor: colors.surface,
              border: `1px solid ${colors.border.default}`,
              minWidth: 200,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: colors.text.muted, fontWeight: 600, textTransform: 'uppercase' }}
            >
              Select Theme
            </Typography>
          </Box>
          <Divider sx={{ borderColor: colors.border.default }} />

          {availableThemes.map((theme) => {
            const ThemeIcon = THEME_ICONS[theme.id] || PaletteIcon;
            const isSelected = theme.id === themeId;

            return (
              <MenuItem
                key={theme.id}
                onClick={() => handleSelectTheme(theme.id)}
                selected={isSelected}
                sx={{
                  py: 1.5,
                  '&.Mui-selected': {
                    bgcolor: colors.success.subtle,
                    '&:hover': {
                      bgcolor: colors.success.subtleHover,
                    },
                  },
                  '&:hover': {
                    bgcolor: colors.hover.background,
                  },
                }}
              >
                <ListItemIcon sx={{ color: isSelected ? colors.success.main : colors.text.secondary }}>
                  <ThemeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={theme.name}
                  secondary={theme.description}
                  primaryTypographyProps={{
                    sx: { color: colors.text.primary, fontWeight: isSelected ? 600 : 400 },
                  }}
                  secondaryTypographyProps={{
                    sx: { color: colors.text.muted, fontSize: '0.75rem' },
                  }}
                />
                {isSelected && (
                  <CheckIcon fontSize="small" sx={{ color: colors.success.main, ml: 1 }} />
                )}
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </>
  );
};

/**
 * Simple theme toggle button (cycles through themes)
 */
export const ThemeToggleButton = ({ size = 'medium' }) => {
  return <ThemeSelector showMenu={false} size={size} />;
};

export default ThemeSelector;
